import { useEffect, useState } from 'react';
import { NavLink, Link, matchPath, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import { VerifyEmailBanner } from './VerifyEmailBanner.jsx';
import { BrandSwitcher } from './BrandSwitcher.jsx';
import { NotificationBell } from './NotificationBell.jsx';

const creatorNav = [
  { to: '/dashboard/creator', label: 'Overview', end: true },
  { to: '/dashboard/creator/profile', label: 'Public profile', short: 'Profile' },
  { to: '/dashboard/creator/applications', label: 'Applications' },
  { to: '/dashboard/creator/pitches', label: 'Pitches' },
  { to: '/dashboard/creator/partnerships', label: 'Partnerships' },
  { to: '/dashboard/creator/competitions', label: 'Competitions' },
  { to: '/dashboard/creator/earnings', label: 'Earnings' },
  { to: '/dashboard/creator/payouts', label: 'Payouts' },
  { to: '/dashboard/pricing', label: 'Pricing' },
  { to: '/dashboard/account', label: 'Account' },
  { to: '/help', label: 'Help' },
  { to: '/dashboard/keys', label: 'API keys' },
];

const brandNav = [
  { to: '/dashboard/brand', label: 'Overview', end: true },
  { to: '/dashboard/brand/profile', label: 'Brand profile', short: 'Profile' },
  { to: '/dashboard/brand/programs', label: 'Programs' },
  { to: '/dashboard/brand/applications', label: 'Applications' },
  { to: '/dashboard/brand/pitches', label: 'Pitches' },
  { to: '/dashboard/brand/partnerships', label: 'Partnerships' },
  { to: '/dashboard/brand/posts', label: 'Posts' },
  { to: '/dashboard/brand/gifts', label: 'Gifts' },
  { to: '/dashboard/brand/creators', label: 'Find creators' },
  { to: '/dashboard/brand/settle', label: 'Settle & pay' },
  { to: '/dashboard/brand/billing', label: 'Billing' },
  { to: '/dashboard/pricing', label: 'Pricing' },
  { to: '/dashboard/account', label: 'Account' },
  { to: '/help', label: 'Help' },
  { to: '/dashboard/keys', label: 'API keys' },
];

/**
 * Dashboard shell.
 *
 * The side switcher is always visible when a user holds both roles — one
 * account, two hats, no second login.
 */
export function DashboardLayout({ side, title, description, actions, children }) {
  const { user, isCreator, isBrand, logout } = useAuth();
  const nav = side === 'brand' ? brandNav : creatorNav;
  const both = isCreator && isBrand;

  return (
    <div className="flex min-h-dvh flex-col bg-paper">
      <header className="border-b border-rule bg-paper-raised">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-4 px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <svg viewBox="0 0 32 32" className="size-6 rounded" aria-hidden="true">
              <rect width="32" height="32" rx="7" className="fill-accent" />
              <path d="M9 23V9.6c0-.33.27-.6.6-.6H23v3.2h-10.2v3.6H21v3.2h-8.2V23z" fill="#fff" />
            </svg>
            <span className="font-semibold tracking-tight">Affily</span>
          </Link>

          {both && (
            <div
              role="tablist"
              aria-label="Switch dashboard"
              className="flex rounded-md border border-rule-strong bg-paper p-0.5"
            >
              {['creator', 'brand'].map((s) => (
                <Link
                  key={s}
                  role="tab"
                  aria-selected={side === s}
                  to={`/dashboard/${s}`}
                  className={`rounded px-3 py-1 text-xs font-medium capitalize transition-colors ${
                    side === s ? 'bg-ink text-paper' : 'text-ink-muted hover:text-ink'
                  }`}
                >
                  {s}
                </Link>
              ))}
            </div>
          )}

          {side === 'brand' && <BrandSwitcher />}

          <div className="ml-auto flex items-center gap-3">
            <span className="hidden text-sm text-ink-muted sm:inline">{user?.email}</span>
            <NotificationBell />
            <button
              onClick={logout}
              className="rounded-md px-2 py-1 text-sm text-ink-muted hover:bg-paper-sunken hover:text-ink"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <MobileNav nav={nav} />

      <div className="mx-auto flex w-full max-w-7xl flex-1 gap-8 px-4 py-8 sm:px-6">
        <aside className="hidden w-52 shrink-0 lg:block">
          <nav className="sticky top-8 space-y-0.5">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `block rounded-md px-3 py-1.5 text-sm transition-colors ${
                    isActive
                      ? 'bg-accent-soft font-medium text-accent-ink'
                      : 'text-ink-muted hover:bg-paper-sunken hover:text-ink'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="min-w-0 flex-1">
          <VerifyEmailBanner />
          {(title || actions) && (
            <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
              <div>
                {title && <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>}
                {description && <p className="mt-1 text-sm text-ink-muted">{description}</p>}
              </div>
              {actions}
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}

/** On a phone the first three tabs show, on a tablet the first six; the rest sit under More. */
const PHONE_TABS = 3;
const TABLET_TABS = 6;

const tabClass = (active) =>
  `whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs ${
    active ? 'bg-accent-soft font-medium text-accent-ink' : 'text-ink-muted'
  }`;

/**
 * Below the desktop width the side menu becomes a row of tabs under the header,
 * where people look, with whatever does not fit behind More rather than a row
 * to scroll sideways.
 */
function MobileNav({ nav }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const active = nav.findIndex((item) => matchPath({ path: item.to, end: Boolean(item.end) }, pathname));
  // Tabs 4–6 show on a tablet and sit under More on a phone; More lights up
  // when the page open is one it holds.
  const moreActive =
    active >= TABLET_TABS
      ? 'bg-accent-soft font-medium text-accent-ink'
      : active >= PHONE_TABS
        ? 'max-md:bg-accent-soft max-md:font-medium max-md:text-accent-ink'
        : '';
  const shownOnTablet = (i) => (i < PHONE_TABS ? '' : i < TABLET_TABS ? 'hidden md:block' : 'hidden');

  return (
    <nav aria-label="Dashboard" className="sticky top-0 z-20 border-b border-rule bg-paper-raised lg:hidden">
      <div className="mx-auto flex max-w-7xl items-center gap-1 px-2 py-1.5 md:px-4">
        {nav.map((item, i) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `${tabClass(isActive)} ${shownOnTablet(i)}`}
          >
            {item.short ? (
              <>
                <span className="md:hidden">{item.short}</span>
                <span className="hidden md:inline">{item.label}</span>
              </>
            ) : (
              item.label
            )}
          </NavLink>
        ))}
        <div className="relative ml-auto">
          <button
            type="button"
            aria-expanded={open}
            aria-haspopup="true"
            onClick={() => setOpen((o) => !o)}
            className={`flex items-center gap-1 ${tabClass(false)} ${moreActive}`}
          >
            More
            <svg
              viewBox="0 0 12 12"
              className={`size-3 transition-transform ${open ? 'rotate-180' : ''}`}
              aria-hidden="true"
            >
              <path d="M3 4.5 6 7.5 9 4.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
          {open && (
            <>
              <button
                type="button"
                aria-label="Close menu"
                className="fixed inset-0 z-10 cursor-default"
                onClick={() => setOpen(false)}
              />
              <div className="absolute right-0 z-20 mt-1 w-52 rounded-md border border-rule bg-paper-raised p-1 shadow-lg">
                {nav.map((item, i) =>
                  i < PHONE_TABS ? null : (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        `rounded-md px-3 py-2 text-sm ${i < TABLET_TABS ? 'block md:hidden' : 'block'} ${
                          isActive
                            ? 'bg-accent-soft font-medium text-accent-ink'
                            : 'text-ink-muted hover:bg-paper-sunken hover:text-ink'
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  ),
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
